import { useEffect, useState } from 'react'
import "./FavouritesPanel.scss"
import FavoriteIcon from '@mui/icons-material/Favorite';
import ModalCloseBtn from '../../../CommonComponents/ModalButtons/ModalCloseBtn/ModalCloseBtn';
import FavouritesComp from '../FavouritesComp/FavouritesComp';
import { BuyerAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/BuyerAPIHandler';
import { Gig } from '../../../../Models/Gig';
import { CircularProgress, Typography } from '@mui/material';
import { createPortal } from 'react-dom';

interface FavouritesProps {
  openPanel: boolean,
  setOpenPanel: any
}

const FavouritesPanel = (props: FavouritesProps) => {
  const { openPanel, setOpenPanel } = props
  const [scrollPosition, setScrollPosition] = useState(0)
  const [gigList, setGigList] = useState<Gig[]>([])
  const [loading, setLoading] = useState(false);
  const [infoText, setInfoText] = useState("You do not have any items added to wishlist yet.");

  const checkIsMobile = () => {
    return window.innerWidth <= 1023; // Match navbar breakpoint - includes Galaxy Fold opened
  };

  const disableScroll = () => {
    const currentPosition = window.scrollY;
    setScrollPosition(currentPosition);
    
    if (checkIsMobile()) {
      // On mobile: prevent body scroll completely
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentPosition}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      // Prevent overscroll bounce
      document.body.style.overscrollBehavior = "none";
    } else {
      // On desktop: use existing behavior
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentPosition}px`;
    }
  };

  const enableScroll = () => {
    if (checkIsMobile()) {
      // On mobile: restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    } else {
      // On desktop: use existing behavior
      document.body.style.position = "static";
      window.scrollTo(0, scrollPosition);
    }
  };

  // Handle scroll event to prevent background scrolling when panel reaches boundaries
  const handlePanelScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!checkIsMobile()) return;
    
    const target = e.currentTarget;
    const isAtTop = target.scrollTop === 0;
    const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;
    
    // Prevent scroll propagation when at boundaries
    if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Handle touch to prevent scroll chaining
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!checkIsMobile()) return;
    
    const target = e.currentTarget;
    const isAtTop = target.scrollTop === 0;
    const isAtBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 1;
    
    // If at boundaries, prevent default to stop scroll chaining
    if (isAtTop || isAtBottom) {
      // Allow scrolling within the panel, but prevent when at boundaries
      const touch = e.touches[0];
      if (touch) {
        const scrollDirection = (e as any).lastTouchY 
          ? touch.clientY - (e as any).lastTouchY 
          : 0;
        (e as any).lastTouchY = touch.clientY;
        
        // Prevent if trying to scroll past boundaries
        if ((isAtTop && scrollDirection > 0) || (isAtBottom && scrollDirection < 0)) {
          e.preventDefault();
        }
      }
    }
  };

  const closeFavouritesPanel = () => {
    setOpenPanel(false)
  }

  async function getFavouriteGigs() {
    setLoading(true)
    setInfoText("")
    try {
      const gigs = await BuyerAPIHandler.shared.getFavouriteGigs()
      setGigList(gigs)
      setInfoText(gigs.length === 0 ? "You do not have any items added to wishlist yet." : "")
    } catch {
      setInfoText("Something went wrong while fetching your wishlist")
    } finally {
      setLoading(false)
    }
  }

  const removeFromWishList = async (gigId: string) => {
    setLoading(true)
    try {
      await BuyerAPIHandler.shared.removeGigFromFavourites(gigId)
      const updatedlist = gigList.filter(gig => gig.id !== gigId)
      setGigList(updatedlist);
      setInfoText(updatedlist.length === 0 ? "You do not have any items added to wishlist yet." : "")
    } catch {
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if(openPanel) {
      disableScroll()
      getFavouriteGigs()
    } else {
      enableScroll()
    }
    // eslint-disable-next-line
  }, [openPanel])

  // Handle dynamic viewport height changes (e.g., when address bar shows/hides)
  useEffect(() => {
    if (!openPanel || !checkIsMobile()) return;

    const updateHeight = () => {
      const panel = document.querySelector('.favouritePanel.active') as HTMLElement;
      if (panel) {
        const newHeight = `calc(100dvh - 60px)`;
        panel.style.height = newHeight;
        panel.style.minHeight = newHeight;
        panel.style.maxHeight = newHeight;
      }
    };

    // Update on visual viewport resize (handles address bar show/hide)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateHeight);
      window.visualViewport.addEventListener('scroll', updateHeight);
    }

    // Fallback for browsers without visualViewport
    window.addEventListener('resize', updateHeight);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateHeight);
        window.visualViewport.removeEventListener('scroll', updateHeight);
      }
      window.removeEventListener('resize', updateHeight);
    };
  }, [openPanel])

  const favouritesPanelContent = (
    <div 
      className={openPanel ? 'favouritePanel active' : 'favouritePanel'} 
      style={{visibility: openPanel ? 'visible' : 'hidden'}}
      onWheel={handlePanelScroll}
      onTouchMove={handleTouchMove}
    >
        <div className="favouritePanelHead">
          <div className="left">
            <FavoriteIcon />
            <p>Wishlist</p>
          </div>
          <div className="right">
            <ModalCloseBtn handleCancel={closeFavouritesPanel} />
          </div>
        </div>
        <div className="favouritePanelBody">
          {loading ? <CircularProgress thickness={5} size={50} sx={{width: "100%", marginTop: '75px', color:'grey'}}/> : 
          <>
            {gigList.length !== 0 ? <FavouritesComp gigList={gigList} closePanel={closeFavouritesPanel} removeFromWishList={removeFromWishList}/> : 
                <>
                  <Typography variant='h5' fontWeight={1000} marginTop={'75px'}> { infoText }</Typography>
               </>}
          </>}
          
        </div>
    </div>
  );

  // Use portal on mobile to render outside of any stacking context
  if (checkIsMobile() && typeof document !== 'undefined') {
    return createPortal(favouritesPanelContent, document.body);
  }

  return favouritesPanelContent;
}

export default FavouritesPanel